import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { projectsApi, locationsApi, attentionApi, clientsApi } from "@/lib/api";
import { Plus, Film, MapPin, Calendar as CalendarIcon, Clapperboard, ArrowRight, Trash2, Search, X, Archive, ArchiveRestore, ChevronDown, ChevronRight, Building2, Activity } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ClientCombobox from "@/components/ClientCombobox";
import ProjectsClientsSwitch from "@/components/ProjectsClientsSwitch";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import LocationsMultiSelect from "@/components/LocationsMultiSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const COVER_GRADIENTS = {
  amber: "from-amber-500/30 via-amber-700/10 to-zinc-900",
  rose: "from-rose-500/30 via-rose-700/10 to-zinc-900",
  teal: "from-teal-500/30 via-teal-700/10 to-zinc-900",
  violet: "from-violet-500/30 via-violet-700/10 to-zinc-900",
  blue: "from-blue-500/30 via-blue-700/10 to-zinc-900",
};

// Follows the active white-label brand accent via CSS vars set by
// BrandProvider. Applied inline so a single project can flip color when
// a tenant switches without adding N Tailwind variants. Default brand
// (Whiplash) keeps its amber via var(--brand-accent), so this option
// is safe on every theme.
const BRAND_COVER_STYLE = {
  backgroundImage:
    "linear-gradient(135deg, var(--brand-accent-soft) 0%, var(--brand-accent-soft) 30%, rgba(24,24,27,0.9) 75%, rgb(24 24 27) 100%)",
};

const COLOR_OPTIONS = [
  // Inline hex on each swatch so the picker always renders the TRUE
  // palette color — otherwise the amber → brand-accent remap in
  // index.css turns the "amber" swatch into the active brand color
  // and it becomes indistinguishable from "Follow brand".
  { key: "amber",  swatch: "", inlineStyle: { backgroundColor: "#f59e0b" } },
  { key: "rose",   swatch: "", inlineStyle: { backgroundColor: "#f43f5e" } },
  { key: "teal",   swatch: "", inlineStyle: { backgroundColor: "#14b8a6" } },
  { key: "violet", swatch: "", inlineStyle: { backgroundColor: "#8b5cf6" } },
  { key: "blue",   swatch: "", inlineStyle: { backgroundColor: "#3b82f6" } },
  // "Follow brand" — reads var(--brand-accent) live.
  { key: "brand",  swatch: "", inlineStyle: { backgroundColor: "var(--brand-accent)" } },
];

// Small pill styling per project status. Keys mirror backend PROJECT_STATUSES.
// Kept desaturated + high-contrast text so the chip reads over any of the
// COVER_GRADIENTS above without fighting the hero.
const STATUS_CHIP = {
  active:           { label: "Active",           cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  on_hold:          { label: "On Hold",          cls: "bg-amber-500/15   text-amber-300   border-amber-500/40" },
  completed:        { label: "Completed",        cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  ready_to_archive: { label: "Ready To Archive", cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  archived:         { label: "Archived",         cls: "bg-zinc-500/15    text-zinc-300    border-zinc-500/40" },
  cancelled:        { label: "Cancelled",        cls: "bg-rose-500/15    text-rose-300    border-rose-500/40" },
};

export default function ProjectsPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [attentionScores, setAttentionScores] = useState({});
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("all"); // "all" | client_id | "none"
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [addLocOpen, setAddLocOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({
    name: "",
    client: "",
    client_id: null,
    job_number: "",
    director: "",
    location_ids: [],
    start_date: new Date().toISOString().slice(0, 10),
    shoot_date: "",
    description: "",
    cover_color: "amber",
  });

  const loadProjects = async () => {
    setLoading(true);
    try {
      const [pData, lData, cData] = await Promise.all([
        projectsApi.list(),
        locationsApi.list(),
        clientsApi.list().catch(() => []),
      ]);
      setProjects(pData);
      setLocations(lData);
      setClients(cData);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
    // Attention scores are best-effort — never block the page on them.
    try {
      const s = await attentionApi.scores();
      setAttentionScores(s?.scores || {});
    } catch {
      /* silent — chips just won't render */
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      const created = await projectsApi.create(form);
      setProjects((p) => [created, ...p]);
      setOpen(false);
      setForm({
        name: "",
        client: "",
        client_id: null,
        job_number: "",
        director: "",
        location_ids: [],
        start_date: new Date().toISOString().slice(0, 10),
        shoot_date: "",
        description: "",
        cover_color: "amber",
      });
      toast.success(`Project "${created.name}" created`);
    } catch (e) {
      toast.error("Failed to create project");
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await projectsApi.remove(id);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success(`Deleted "${name}"`);
    } catch (e) {
      toast.error("Failed to delete project");
    }
  };

  // Archive / restore both patch the status field. We optimistic-update so
  // the card slides into (or out of) the Archived section immediately;
  // any server error rolls back via `load()`.
  const handleArchive = async (id, name) => {
    setProjects((prev) =>
      prev.map((x) => (x.id === id ? { ...x, status: "archived" } : x)),
    );
    try {
      await projectsApi.update(id, { status: "archived" });
      toast.success(`Archived "${name}"`);
    } catch (e) {
      toast.error("Failed to archive project");
      loadProjects();
    }
  };

  const handleRestore = async (id, name) => {
    setProjects((prev) =>
      prev.map((x) => (x.id === id ? { ...x, status: "active" } : x)),
    );
    try {
      await projectsApi.update(id, { status: "active" });
      toast.success(`Restored "${name}" to Active`);
    } catch (e) {
      toast.error("Failed to restore project");
      loadProjects();
    }
  };

  // Split archived out so the main grid stays clean. Archived projects
  // live in a dedicated collapsible section below. Client filter runs
  // BEFORE the search filter — search operates on the client-scoped
  // subset — matching what the user expects when narrowing down.
  const clientScoped = useMemo(() => {
    if (clientFilter === "all") return projects;
    if (clientFilter === "none") {
      // Projects with no linked client — useful for triaging legacy rows
      return projects.filter((p) => !(p.client_id || "").trim());
    }
    return projects.filter((p) => p.client_id === clientFilter);
  }, [projects, clientFilter]);

  const activeProjects = useMemo(
    () => clientScoped.filter((p) => p.status !== "archived"),
    [clientScoped],
  );
  const archivedProjects = useMemo(
    () => clientScoped.filter((p) => p.status === "archived"),
    [clientScoped],
  );

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeProjects;
    // For Job # matching, strip everything that isn't alphanumeric
    // so users can find "WC-2604" by typing "wc2604", "#wc-2604", "WC 2604", etc.
    const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const qNorm = normalize(q);
    return activeProjects.filter((p) => {
      if (p.job_number && qNorm && normalize(p.job_number).includes(qNorm)) return true;
      const haystack = [p.name, p.director, p.description, p.location]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [activeProjects, query]);

  const filteredArchivedProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return archivedProjects;
    const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const qNorm = normalize(q);
    return archivedProjects.filter((p) => {
      if (p.job_number && qNorm && normalize(p.job_number).includes(qNorm)) return true;
      const haystack = [p.name, p.director, p.description, p.location]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [archivedProjects, query]);

  const [showArchived, setShowArchived] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="cinematic-hero relative border-b border-zinc-800/60">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20 pb-16 relative">
          <div className="text-overline text-amber-500/90 mb-5">Pre-Production · Shot List Organizer</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-zinc-50 max-w-3xl leading-[1.05]">
            Plan every frame.<br />
            <span className="italic font-extralight text-zinc-300">Before the camera rolls.</span>
          </h1>
          <p className="mt-6 text-zinc-400 text-base max-w-xl leading-relaxed">
            Organize shots, schedule scenes, attach storyboards, and generate
            cinematic shot lists with AI — built for directors, DPs, and producers.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <ProjectsClientsSwitch />
            {isAdmin && (
              <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="new-project-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold tracking-wide bg-amber-500 text-black hover:bg-amber-400 rounded-sm transition-all"
                >
                  <Plus className="w-4 h-4" />
                  New Project
                </button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl font-light">
                    New Project
                  </DialogTitle>
                  <DialogDescription className="text-zinc-500">
                    Set up a new shoot. You can edit details anytime.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-2">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-1">
                      <Label htmlFor="job_number" className="text-overline text-zinc-500">
                        Job #
                      </Label>
                      <Input
                        id="job_number"
                        data-testid="project-job-number-input"
                        value={form.job_number}
                        onChange={(e) => setForm({ ...form, job_number: e.target.value })}
                        placeholder="2026-014"
                        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 font-mono"
                      />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label htmlFor="name" className="text-overline text-zinc-500">
                        Project Name
                      </Label>
                      <Input
                        id="name"
                        data-testid="project-name-input"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Untitled Feature, Ep. 01"
                        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="director" className="text-overline text-zinc-500">
                        Director
                      </Label>
                      <Input
                        id="director"
                        data-testid="project-director-input"
                        value={form.director}
                        onChange={(e) => setForm({ ...form, director: e.target.value })}
                        placeholder="A. Tarkovsky"
                        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="start_date" className="text-overline text-zinc-500">
                        Start Date <span className="text-amber-500/80 normal-case">required</span>
                      </Label>
                      <Input
                        id="start_date"
                        data-testid="project-start-date-input"
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                      />
                      <p className="text-[10px] text-zinc-600 mt-1">Day 0 for the plan + studio anchor</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="shoot_date" className="text-overline text-zinc-500">
                        Shoot Date <span className="text-zinc-600 normal-case">optional</span>
                      </Label>
                      <Input
                        id="shoot_date"
                        data-testid="project-date-input"
                        type="date"
                        value={form.shoot_date}
                        onChange={(e) => setForm({ ...form, shoot_date: e.target.value })}
                        className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
                      />
                      <p className="text-[10px] text-zinc-600 mt-1">Leave blank — add later from the project</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-overline text-zinc-500">Client</Label>
                    <ClientCombobox
                      testIdPrefix="project-client"
                      value={form.client_id}
                      initialName={form.client}
                      onChange={({ id, name }) =>
                        setForm({ ...form, client_id: id, client: name || "" })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-overline text-zinc-500">
                      Locations
                    </Label>
                    <LocationsMultiSelect
                      value={form.location_ids}
                      onChange={(ids) => setForm({ ...form, location_ids: ids })}
                      locations={locations}
                      onAddNew={() => setAddLocOpen(true)}
                      onLocationUpdated={loadProjects}
                      testIdPrefix="project-loc"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="desc" className="text-overline text-zinc-500">
                      Description
                    </Label>
                    <Textarea
                      id="desc"
                      data-testid="project-desc-input"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Brief logline or production notes…"
                      className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 min-h-[80px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-overline text-zinc-500">Cover Tone</Label>
                    <div className="flex gap-2">
                      {COLOR_OPTIONS.map((c) => (
                        <button
                          type="button"
                          key={c.key}
                          data-testid={`color-${c.key}`}
                          onClick={() => setForm({ ...form, cover_color: c.key })}
                          style={c.inlineStyle}
                          title={c.key === "brand" ? "Follow brand" : c.key}
                          className={`w-8 h-8 rounded-sm transition-all ${c.swatch} ${
                            form.cover_color === c.key
                              ? "ring-2 ring-offset-2 ring-offset-zinc-950 ring-amber-500"
                              : "opacity-60 hover:opacity-100"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <DialogFooter className="pt-4">
                    <Button
                      type="submit"
                      data-testid="create-project-submit"
                      className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
                    >
                      Create Project
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}

            <div className="text-sm text-zinc-500 ml-2 font-mono">
              {activeProjects.length} {activeProjects.length === 1 ? "project" : "projects"}
              {archivedProjects.length > 0 && (
                <span className="text-zinc-600"> · {archivedProjects.length} archived</span>
              )}
              <span className="text-zinc-600"> · v0.1</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Grid */}
      <section className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <h2 className="font-heading text-2xl font-medium text-zinc-100">
              Productions
            </h2>
            <div className="text-overline text-zinc-500 mt-1">
              {clientFilter !== "all" && (
                <span
                  data-testid="client-filter-active-label"
                  className="text-amber-400 mr-2"
                >
                  {clientFilter === "none"
                    ? "Unlinked · "
                    : `${clients.find((c) => c.id === clientFilter)?.name || "Client"} · `}
                </span>
              )}
              {query.trim()
                ? `${filteredProjects.length} of ${activeProjects.length} match`
                : "Recently updated"}
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
            <ClientFilterDropdown
              value={clientFilter}
              onChange={setClientFilter}
              clients={clients}
            />
            <div className="relative flex-1 sm:w-80 min-w-[220px]">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="projects-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, Job #, director…"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9 pr-9"
              />
              {query && (
                <button
                  data-testid="projects-search-clear"
                  onClick={() => setQuery("")}
                  title="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-56 rounded-md bg-zinc-900/60 border border-zinc-800 animate-pulse"
              />
            ))}
          </div>
        ) : activeProjects.length === 0 ? (
          isAdmin ? (
            <EmptyState onCreate={() => setOpen(true)} />
          ) : (
            <ViewerEmptyState />
          )
        ) : filteredProjects.length === 0 ? (
          <NoMatchesState query={query} onClear={() => setQuery("")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((p, idx) => (
              <ProjectCard
                key={p.id}
                p={p}
                idx={idx}
                isAdmin={isAdmin}
                attention={attentionScores[p.id]}
                onArchive={handleArchive}
                onRestore={handleRestore}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </section>
      {/* Archived section — collapsed by default, only rendered when
          there's at least one archived project. Search input applies to
          both active and archived lists. */}
      {archivedProjects.length > 0 && (
        <section
          className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-24"
          data-testid="archived-section"
        >
          <button
            type="button"
            data-testid="archived-section-toggle"
            onClick={() => setShowArchived((v) => !v)}
            className="w-full flex items-center justify-between gap-4 py-4 border-t border-zinc-800/80 text-left group"
          >
            <div className="flex items-center gap-3">
              {showArchived ? (
                <ChevronDown className="w-4 h-4 text-zinc-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              )}
              <div>
                <h2
                  data-testid="archived-section-heading"
                  className="font-heading text-lg font-medium text-zinc-300 group-hover:text-amber-400 transition-colors"
                >
                  Archived
                </h2>
                <div className="text-overline text-zinc-500 mt-0.5">
                  {query.trim()
                    ? `${filteredArchivedProjects.length} of ${archivedProjects.length} match`
                    : `${archivedProjects.length} project${archivedProjects.length === 1 ? "" : "s"} · read-only unless restored`}
                </div>
              </div>
            </div>
            <Archive className="w-4 h-4 text-zinc-600 group-hover:text-amber-400 transition-colors" />
          </button>
          {showArchived && (
            filteredArchivedProjects.length === 0 ? (
              <div
                data-testid="archived-no-matches"
                className="text-sm text-zinc-500 italic py-6"
              >
                No archived projects match &ldquo;{query}&rdquo;.
              </div>
            ) : (
              <div
                data-testid="archived-grid"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-90"
              >
                {filteredArchivedProjects.map((p, idx) => (
                  <ProjectCard
                    key={p.id}
                    p={p}
                    idx={idx}
                    isAdmin={isAdmin}
                    attention={null}
                    onArchive={handleArchive}
                    onRestore={handleRestore}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )
          )}
        </section>
      )}
      <AddLocationInlineDialog
        open={addLocOpen}
        onOpenChange={setAddLocOpen}
        onCreated={(loc) => {
          setLocations((prev) =>
            [...prev, loc].sort((a, b) => a.name.localeCompare(b.name))
          );
          setForm((f) => ({
            ...f,
            location_ids: [...(f.location_ids || []), loc.id],
          }));
          setAddLocOpen(false);
          toast.success(`Added "${loc.name}" to library`);
        }}
      />
    </div>
  );
}

function AddLocationInlineDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    address: "",
    parking_info: "",
    nearest_hospital: "",
    contact_name: "",
    contact_phone: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    setSaving(true);
    try {
      const loc = await locationsApi.create(form);
      onCreated(loc);
      setForm({
        name: "",
        address: "",
        parking_info: "",
        nearest_hospital: "",
        contact_name: "",
        contact_phone: "",
      });
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            New Location
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Saved to the Locations library &mdash; reusable on any project or call sheet.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Name<span className="text-amber-500 ml-1">*</span>
            </Label>
            <Input
              data-testid="inline-loc-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Studio A, Brooklyn"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Address</Label>
            <AddressAutocomplete
              testId="inline-loc-address"
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              placeholder="Start typing an address…"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Parking</Label>
              <Input
                data-testid="inline-loc-parking"
                value={form.parking_info}
                onChange={(e) => setForm({ ...form, parking_info: e.target.value })}
                placeholder="Free lot"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Hospital</Label>
              <Input
                data-testid="inline-loc-hospital"
                value={form.nearest_hospital}
                onChange={(e) => setForm({ ...form, nearest_hospital: e.target.value })}
                placeholder="Methodist Dallas…"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Contact</Label>
              <Input
                data-testid="inline-loc-contact"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Phone</Label>
              <Input
                data-testid="inline-loc-phone"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="inline-loc-save"
              disabled={saving}
              className="bg-amber-500 text-black hover:bg-amber-400 rounded-sm"
            >
              {saving ? "Saving…" : "Add Location"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-zinc-800 rounded-md p-16 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-zinc-900 border border-zinc-800 mb-5">
        <Clapperboard className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-2xl font-light text-zinc-100 mb-2">
        No productions yet
      </h3>
      <p className="text-zinc-500 mb-6 text-sm">
        Create your first project to start building shot lists.
      </p>
      <button
        data-testid="empty-create-btn"
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm transition-all"
      >
        <Plus className="w-4 h-4" /> Create Project
      </button>
    </div>
  );
}

function ViewerEmptyState() {
  return (
    <div
      data-testid="viewer-empty-state"
      className="border border-dashed border-zinc-800 rounded-md p-16 text-center"
    >
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-zinc-900 border border-zinc-800 mb-5">
        <Clapperboard className="w-7 h-7 text-zinc-600" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-2xl font-light text-zinc-100 mb-2">
        No projects yet
      </h3>
      <p className="text-zinc-500 text-sm max-w-md mx-auto leading-relaxed">
        Your projects will appear here once an admin assigns you. You&apos;ll get
        an email when you&apos;re added to a new project.
      </p>
    </div>
  );
}

function NoMatchesState({ query, onClear }) {
  return (
    <div
      data-testid="projects-no-matches"
      className="border border-dashed border-zinc-800 rounded-md p-12 text-center"
    >
      <Search className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
      <h3 className="font-heading text-xl font-light text-zinc-100 mb-1">
        No projects match &ldquo;{query}&rdquo;
      </h3>
      <p className="text-zinc-500 text-sm mb-5">
        Try a different Job #, name, director, or location.
      </p>
      <button
        data-testid="projects-search-clear-empty"
        onClick={onClear}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 rounded-sm transition-colors"
      >
        Clear search
      </button>
    </div>
  );
}

// Small colored dot + score on the top-right of each project card. Only
// rendered when the backend returns a non-zero score for the project.
// Colors map: low = emerald, medium = amber, high = rose.
const ATTENTION_STYLES = {
  low:    { dot: "bg-emerald-400", chip: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  medium: { dot: "bg-amber-400",   chip: "bg-amber-500/15   border-amber-500/40   text-amber-300"   },
  high:   { dot: "bg-rose-400 animate-pulse", chip: "bg-rose-500/15 border-rose-500/40 text-rose-300" },
};

function AttentionChip({ projectId, data }) {
  const style = ATTENTION_STYLES[data.level] || ATTENTION_STYLES.low;
  const tooltip = data.reasons?.length
    ? `Attention: ${data.score}\n${data.reasons.join("\n")}`
    : `Attention score: ${data.score}`;
  return (
    <div
      data-testid={`attention-chip-${projectId}`}
      data-attention-level={data.level}
      data-attention-score={data.score}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 pl-1.5 pr-2 py-0.5 rounded-full text-[10px] font-mono font-bold border ${style.chip}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {data.score}
    </div>
  );
}


// One card in the projects grid — same shape whether it's active or
// archived. Kept as a plain function inside this file so it closes over
// the module-level COVER_GRADIENTS / STATUS_CHIP tables without prop
// drilling them. Archived cards swap the top-right Archive button for a
// Restore button; delete stays for both.
function ProjectCard({ p, idx, isAdmin, attention, onArchive, onRestore, onDelete }) {
  const chip = STATUS_CHIP[p.status] || STATUS_CHIP.active;
  const isArchived = p.status === "archived";
  return (
    <div
      data-testid={`project-card-${p.id}`}
      data-project-status={p.status || "active"}
      style={{ animationDelay: `${idx * 60}ms` }}
      className="animate-fade-up group relative rounded-md overflow-hidden border border-zinc-800 bg-zinc-900 hover:border-zinc-700 transition-all"
    >
      <Link to={`/projects/${p.id}`} className="block">
        <div
          className={`h-32 relative overflow-hidden ${
            p.cover_color === "brand"
              ? ""
              : `bg-gradient-to-br ${COVER_GRADIENTS[p.cover_color] || COVER_GRADIENTS.amber}`
          } ${isArchived ? "grayscale opacity-70" : ""}`}
          style={p.cover_color === "brand" ? BRAND_COVER_STYLE : undefined}
        >
          <div className="absolute inset-0 flex items-center justify-center opacity-20">
            <Clapperboard className="w-24 h-24 text-zinc-100" strokeWidth={1} />
          </div>
          <div
            className="absolute top-3 left-3 text-overline text-zinc-300/80"
            data-testid={`project-card-subtitle-${p.id}`}
          >
            {p.shoot_date
              ? `Shoot · ${p.shoot_date}`
              : p.start_date
              ? `Start · ${p.start_date} · No shoots scheduled`
              : "Pre-production"}
          </div>
          {p.day_count > 1 && (
            <div
              data-testid={`project-day-count-${p.id}`}
              className="absolute bottom-2 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider bg-zinc-950/70 text-amber-300 border border-amber-500/40 backdrop-blur"
              title={`Multi-day shoot · ${p.day_count} shoot days`}
            >
              {p.day_count} shoot days
            </div>
          )}
          {!isArchived && p.in_progress_count > 0 && (
            <div
              data-testid={`project-in-progress-count-${p.id}`}
              className="absolute bottom-2 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider bg-zinc-950/70 text-sky-300 border border-sky-500/40 backdrop-blur"
              title={`${p.in_progress_count} task${p.in_progress_count === 1 ? "" : "s"} currently in progress`}
            >
              <Activity className="w-3 h-3" strokeWidth={2.5} />
              {p.in_progress_count} in progress
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            {p.job_number ? (
              <div className="text-overline text-amber-500/80 font-mono">
                Job #{p.job_number}
              </div>
            ) : (
              <span aria-hidden />
            )}
            <div
              data-testid={`project-card-status-${p.id}`}
              className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider border ${chip.cls}`}
              title={`Status: ${chip.label}`}
            >
              {chip.label}
            </div>
          </div>
          <h3 className="font-heading text-xl text-zinc-100 mb-1.5 truncate group-hover:text-amber-500 transition-colors">
            {p.name}
          </h3>
          {p.client && (
            <div
              data-testid={`project-client-${p.id}`}
              className="text-xs text-zinc-400 mb-1.5 truncate"
            >
              for <span className="text-zinc-200">{p.client}</span>
            </div>
          )}
          <p className="text-sm text-zinc-500 line-clamp-2 min-h-[2.5rem]">
            {p.description || "No description"}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs text-zinc-500">
            {p.director && (
              <div className="flex items-center gap-1.5">
                <Film className="w-3.5 h-3.5" />
                <span>{p.director}</span>
              </div>
            )}
            {attention && attention.score > 0 && (
              <AttentionChip projectId={p.id} data={attention} />
            )}
            {p.location && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                <span className="truncate max-w-[120px]">{p.location}</span>
              </div>
            )}
          </div>
        </div>
      </Link>
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5">
        {isAdmin && !isArchived && (
          <button
            data-testid={`archive-project-${p.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onArchive(p.id, p.name);
            }}
            title="Archive project"
            className="p-1.5 rounded-sm bg-zinc-900/80 backdrop-blur border border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40"
          >
            <Archive className="w-3.5 h-3.5" />
          </button>
        )}
        {isAdmin && isArchived && (
          <button
            data-testid={`restore-project-${p.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRestore(p.id, p.name);
            }}
            title="Restore to Active"
            className="p-1.5 rounded-sm bg-zinc-900/80 backdrop-blur border border-zinc-800 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/40"
          >
            <ArchiveRestore className="w-3.5 h-3.5" />
          </button>
        )}
        {isAdmin && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                data-testid={`delete-project-${p.id}`}
                className="p-1.5 rounded-sm bg-zinc-900/80 backdrop-blur border border-zinc-800 text-zinc-400 hover:text-rose-400 hover:border-rose-500/40"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete &ldquo;{p.name}&rdquo;?</AlertDialogTitle>
                <AlertDialogDescription className="text-zinc-500">
                  All shots in this project will be permanently removed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`confirm-delete-${p.id}`}
                  onClick={() => onDelete(p.id, p.name)}
                  className="bg-rose-500 text-zinc-50 hover:bg-rose-600"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <Link
        to={`/projects/${p.id}`}
        className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-amber-500 text-xs flex items-center gap-1"
      >
        Open <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}


// Compact "Filter by Client" dropdown for the Projects header. Uses a
// native <select> so it stays keyboard-accessible and mobile-friendly
// without pulling in another shadcn wrapper. Emits three semantic
// values: "all" (no filter), "none" (projects with no linked client),
// or a specific client_id.
function ClientFilterDropdown({ value, onChange, clients }) {
  return (
    <div className="relative w-full sm:w-auto min-w-0">
      <Building2 className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <select
        data-testid="client-filter-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // `max-w-full` prevents the native <select> from growing wider
        // than the container when a client has a long name (iOS Safari
        // sizes the closed select to fit its widest <option> by default).
        className="appearance-none pl-9 pr-8 py-2 text-sm rounded-md bg-zinc-950 border border-zinc-800 hover:border-zinc-700 focus:border-amber-500 focus:outline-none text-zinc-100 w-full sm:w-auto sm:min-w-[180px] max-w-full truncate"
        aria-label="Filter by client"
      >
        <option value="all">All Clients</option>
        <option value="none">— No client linked —</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <ChevronDown className="w-3.5 h-3.5 text-zinc-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      {value !== "all" && (
        <button
          type="button"
          data-testid="client-filter-clear"
          onClick={() => onChange("all")}
          title="Clear client filter"
          className="absolute -right-1 -top-1 p-0.5 rounded-full bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-amber-400 hover:border-amber-500/40"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

